import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Textarea } from "@/components/ui/textarea"
import { AnimatedCard } from "./AnimatedCard"
import { TierBadge } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  Heart, 
  MessageCircle, 
  Share2,
  Bookmark,
  MoreHorizontal,
  Camera,
  MapPin,
  Crown,
  Star,
  Zap,
  Users,
  Verified
} from "lucide-react"

interface SocialPost {
  id: string
  user: {
    id: string
    name: string
    username: string
    tier: 'initiate' | 'creator' | 'architect'
    avatar: string
    isVerified?: boolean
    followerCount?: number
  }
  content: {
    text: string
    images?: string[]
    location?: string
    tags: string[]
    mentions: string[]
  }
  engagement: {
    likes: number
    comments: number
    shares: number
    saves: number
  }
  interactions: {
    isLiked: boolean
    isBookmarked: boolean
    isFollowing: boolean
  }
  metadata: {
    timestamp: string
    isSponsored?: boolean
    isFeatured?: boolean
    challengeId?: string
    productMentions?: Array<{
      id: string
      name: string
      price: number
    }>
  }
}

interface Comment {
  id: string
  user: {
    name: string
    username: string
    avatar: string
    tier: 'initiate' | 'creator' | 'architect'
  }
  text: string
  timestamp: string
  likes: number
  isLiked: boolean
}

interface SocialFeedProps {
  className?: string
}

const mockPosts: SocialPost[] = [
  {
    id: "1",
    user: {
      id: "user1",
      name: "Maya Rodriguez",
      username: "@maya_moves",
      tier: "architect",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop",
      isVerified: true,
      followerCount: 15200
    },
    content: {
      text: "Morning flow in the new Architect Hoodie ✨ The fabric moves like a second skin. Perfect for those early training sessions when the city's still quiet. #WearingGameshape #ArchitectCollection",
      images: [
        "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600&h=600&fit=crop",
        "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=600&fit=crop"
      ],
      location: "Brooklyn Bridge Park",
      tags: ["WearingGameshape", "ArchitectCollection", "MorningFlow"],
      mentions: ["@gameshape"]
    },
    engagement: {
      likes: 234,
      comments: 18,
      shares: 12,
      saves: 45
    },
    interactions: {
      isLiked: false,
      isBookmarked: true,
      isFollowing: true
    },
    metadata: {
      timestamp: "2 hours ago",
      isFeatured: true,
      productMentions: [
        {
          id: "architect-hoodie",
          name: "Architect Hoodie",
          price: 220
        }
      ]
    }
  },
  {
    id: "2",
    user: {
      id: "user2",
      name: "Jordan Kim",
      username: "@jordan_urban",
      tier: "creator",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop",
      followerCount: 8500
    },
    content: {
      text: "Submitted my entry for the Motion & Form challenge! 📸 Caught this shot during yesterday's run through the city. The Kinetic Joggers handle every stride perfectly. What do you think?",
      images: [
        "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=600&h=600&fit=crop"
      ],
      location: "Manhattan",
      tags: ["MotionAndForm", "KineticJoggers", "UrbanExploration"],
      mentions: []
    },
    engagement: {
      likes: 127,
      comments: 24,
      shares: 8,
      saves: 19
    },
    interactions: {
      isLiked: true,
      isBookmarked: false,
      isFollowing: false
    },
    metadata: {
      timestamp: "4 hours ago",
      challengeId: "motion-form-challenge",
      productMentions: [
        {
          id: "kinetic-joggers",
          name: "Apex Kinetic Joggers", 
          price: 180
        }
      ]
    }
  },
  {
    id: "3",
    user: {
      id: "user3",
      name: "Alex Chen",
      username: "@alex_shapes",
      tier: "creator",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop",
      followerCount: 6200
    },
    content: {
      text: "Sunday reset vibes 🧘‍♀️ Taking time to slow down and appreciate the details. The Form Tank's minimalist design perfectly matches my mindful movement practice.",
      images: [
        "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=600&h=600&fit=crop"
      ],
      location: "Home Studio",
      tags: ["SundayReset", "MindfulMovement", "FormTank"],
      mentions: []
    },
    engagement: {
      likes: 89,
      comments: 12,
      shares: 5,
      saves: 28
    },
    interactions: {
      isLiked: false,
      isBookmarked: false,
      isFollowing: true
    },
    metadata: {
      timestamp: "1 day ago",
      productMentions: [
        {
          id: "form-tank",
          name: "Form Training Tank",
          price: 85
        }
      ]
    }
  }
]

export function SocialFeed({ className }: SocialFeedProps) {
  const { state, actions } = useApexState()
  const [posts, setPosts] = useState<SocialPost[]>(mockPosts)
  const [expandedComments, setExpandedComments] = useState<string | null>(null)
  const [newComment, setNewComment] = useState("")

  const toggleLike = (postId: string) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? {
            ...post,
            engagement: {
              ...post.engagement,
              likes: post.interactions.isLiked 
                ? post.engagement.likes - 1 
                : post.engagement.likes + 1
            },
            interactions: {
              ...post.interactions,
              isLiked: !post.interactions.isLiked
            }
          }
        : post
    ))
  }

  const toggleBookmark = (postId: string) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? {
            ...post,
            interactions: {
              ...post.interactions,
              isBookmarked: !post.interactions.isBookmarked
            }
          }
        : post
    ))
  }

  const toggleFollow = (postId: string) => {
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? {
            ...post,
            interactions: {
              ...post.interactions,
              isFollowing: !post.interactions.isFollowing
            }
          }
        : post
    ))
  }

  const handleShare = (post: SocialPost) => {
    console.log('Sharing post:', post.id)
    // Add share logic
  }

  const getTierBadgeStyle = (tier: string) => {
    switch (tier) {
      case 'architect':
        return 'tier-badge-architect text-xs px-2 py-0.5'
      case 'creator':
        return 'tier-badge-creator text-xs px-2 py-0.5'
      default:
        return 'tier-badge-initiate text-xs px-2 py-0.5'
    }
  }

  const formatEngagementCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`
    }
    return count.toString()
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {posts.map(post => (
        <AnimatedCard key={post.id}>
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-0">
              {/* Post Header */}
              <div className="p-4 pb-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={post.user.avatar} alt={post.user.name} />
                      <AvatarFallback>{post.user.name.slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-foreground">{post.user.name}</h4>
                        {post.user.isVerified && (
                          <Verified size={14} className="text-primary fill-current" />
                        )}
                        <Badge className={getTierBadgeStyle(post.user.tier)}>
                          {post.user.tier}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{post.user.username}</span>
                        <span>•</span>
                        <span>{post.metadata.timestamp}</span>
                        {post.user.followerCount && (
                          <>
                            <span>•</span>
                            <span>{formatEngagementCount(post.user.followerCount)} followers</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {post.metadata.isFeatured && (
                      <Badge className="bg-primary text-black">
                        <Star size={10} className="mr-1" />
                        Featured
                      </Badge>
                    )}
                    
                    {!post.interactions.isFollowing && post.user.id !== state.user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleFollow(post.id)}
                      >
                        Follow
                      </Button>
                    )}
                    
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="px-4 py-3">
                <p className="text-foreground leading-relaxed whitespace-pre-line">
                  {post.content.text}
                </p>
                
                {/* Location */}
                {post.content.location && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                    <MapPin size={12} />
                    <span>{post.content.location}</span>
                  </div>
                )}

                {/* Product Mentions */}
                {post.metadata.productMentions && post.metadata.productMentions.length > 0 && (
                  <div className="mt-3">
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                      {post.metadata.productMentions.map(product => (
                        <div
                          key={product.id}
                          className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/30 transition-colors min-w-fit"
                        >
                          <Crown size={12} className="text-primary" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{product.name}</p>
                            <p className="text-xs text-primary">${product.price}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Post Images */}
              {post.content.images && post.content.images.length > 0 && (
                <div className={`grid gap-1 ${post.content.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {post.content.images.map((image, index) => (
                    <div
                      key={index}
                      className={`aspect-square overflow-hidden ${post.content.images!.length === 1 ? 'aspect-[4/3]' : ''}`}
                    >
                      <img
                        src={image}
                        alt={`Post image ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Challenge Badge */}
              {post.metadata.challengeId && (
                <div className="px-4 py-2 border-t border-border/50">
                  <div className="flex items-center gap-2 text-sm text-accent">
                    <Camera size={14} />
                    <span>Challenge Entry: Motion & Form</span>
                  </div>
                </div>
              )}

              {/* Engagement Actions */}
              <div className="p-4 border-t border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <button
                      onClick={() => toggleLike(post.id)}
                      className="flex items-center gap-2 group transition-colors"
                    >
                      <Heart 
                        size={20} 
                        className={`group-hover:scale-110 transition-transform ${
                          post.interactions.isLiked 
                            ? "fill-red-400 text-red-400" 
                            : "hover:text-red-400"
                        }`}
                      />
                      <span className="text-sm text-muted-foreground">
                        {formatEngagementCount(post.engagement.likes)}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => setExpandedComments(expandedComments === post.id ? null : post.id)}
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <MessageCircle size={20} />
                      <span className="text-sm text-muted-foreground">
                        {formatEngagementCount(post.engagement.comments)}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => handleShare(post)}
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <Share2 size={20} />
                      <span className="text-sm text-muted-foreground">
                        {formatEngagementCount(post.engagement.shares)}
                      </span>
                    </button>
                  </div>

                  <button
                    onClick={() => toggleBookmark(post.id)}
                    className="hover:text-primary transition-colors"
                  >
                    <Bookmark 
                      size={20} 
                      className={post.interactions.isBookmarked ? "fill-current text-primary" : ""}
                    />
                  </button>
                </div>

                {/* Engagement Summary */}
                {post.engagement.likes > 0 && (
                  <div className="mt-3 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {formatEngagementCount(post.engagement.likes)}
                    </span>
                    <span> likes</span>
                    {post.engagement.saves > 0 && (
                      <>
                        <span> • </span>
                        <span className="font-medium text-foreground">
                          {formatEngagementCount(post.engagement.saves)}
                        </span>
                        <span> saves</span>
                      </>
                    )}
                  </div>
                )}

                {/* Comments Section */}
                {expandedComments === post.id && (
                  <div className="mt-4 space-y-4">
                    {/* Add Comment */}
                    <div className="flex gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={state.user?.avatar} alt={state.user?.name} />
                        <AvatarFallback>{state.user?.name?.slice(0, 2)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 flex gap-2">
                        <Textarea
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          className="min-h-[36px] resize-none bg-muted/20"
                        />
                        <Button 
                          size="sm"
                          disabled={!newComment.trim()}
                          className="self-end"
                        >
                          Post
                        </Button>
                      </div>
                    </div>

                    {/* Mock Comments */}
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=32&h=32&fit=crop" />
                          <AvatarFallback>SM</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="bg-muted/20 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground text-sm">Sarah M.</span>
                              <TierBadge tier="creator" size="xs" />
                              <span className="text-xs text-muted-foreground">2h</span>
                            </div>
                            <p className="text-sm text-foreground">
                              Love this shot! The composition is perfect 🔥
                            </p>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <button className="hover:text-foreground">Like</button>
                            <button className="hover:text-foreground">Reply</button>
                            <span>12 likes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>
      ))}
    </div>
  )
}